// react-router-dom components
import { Link, useNavigate } from "react-router-dom";

// @mui material components
import Checkbox from "@mui/material/Checkbox";
import { AppBar, Tab, Tabs } from "@mui/material";

// Argon Dashboard 2 MUI components
import Box from "themeComponents/Box";
import Button from "themeComponents/Button";
import Typography from "themeComponents/Typography";

// Authentication layout components
import FormikInput from "components/formikInput";
import { Form, Formik, FormikHelpers } from "formik";
import CoverLayout from "layouts/authentication/components/CoverLayout";
import { useEffect, useState } from "react";
import * as Yup from "yup";

// Argon Dashboard 2 MUI base styles
import "App.css";
import breakpoints from "assets/theme/base/breakpoints";
import { ProfileUpload } from "components/uploadButton";
import useAuth from "hooks/useAuth";
import useIndustry from "hooks/useIndustry";

interface SignUpValues {
  email: string;
  password: string;
  firstname: string;
  lastname: string;
  telephone1: string;
  companyName: string;
  industry: string;
}

function Cover(): React.JSX.Element {
  const {
    handleSignupSubmit,
    loading,
    data,
    setData,
    signupAccountRole,
    handleSetTabValue,
    signUpFields,
  } = useAuth();
  const { getAllIndustries } = useIndustry();

  const [tabsOrientation, setTabsOrientation] = useState<"horizontal" | "vertical">("horizontal");
  const navigator = useNavigate();
  const domainName = window.location.hostname;

  useEffect(() => {
    // A function that sets the orientation state of the tabs.
    function handleTabsOrientation(): void {
      setTabsOrientation(
        window.innerWidth < breakpoints.values.sm ? "vertical" : "horizontal"
      );
    }

    /**
     The event listener that's calling the handleTabsOrientation function when resizing the window.
    */
    window.addEventListener("resize", handleTabsOrientation);

    // Call the handleTabsOrientation function to set the state with the initial value.
    handleTabsOrientation();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, []);

  useEffect(() => {
    if (domainName !== (process.env.REACT_APP_CLIENT_DOMAIN || '')) {
      navigator("/authentication/sign-in");
    }
  }, [domainName, navigator]);

  useEffect(() => {
    if (signupAccountRole === "company") {
      getAllIndustries();
    }
  }, [signupAccountRole, getAllIndustries]);

  return (
    <CoverLayout maxWidth={520}>
      <Formik
            initialValues={{
              email: "",
              password: "",
              firstname: "",
              lastname: "",
              telephone1: "",
              companyName: "",
              industry: "",
            }}
            validationSchema={Yup.object({
              firstname: Yup.string().required("First Name is required"),
              lastname: Yup.string().required("Last Name is required"),
              telephone1: Yup.string().required("Phone Number is required"),
              companyName: Yup.string().test(
                "company-required",
                "Company Name is required",
                function (value) {
                    if (signupAccountRole !== "creator" && signupAccountRole !== "employee" && !value)
                    return false;
                  return true;
                }
              ),
              industry: Yup.string().test(
                "industry-required",
                "Industry is required",
                function (value) {
                    if (signupAccountRole !== "creator" && signupAccountRole !== "employee" && !value)
                    return false;
                  return true;
                }
              ),
              email: Yup.string().email("Invalid email").required("Email is required"),
              password: Yup.string().min(6, "Password too short").required("Password is required"),
            })}
            onSubmit={async (values: SignUpValues, { setSubmitting }: FormikHelpers<SignUpValues>) => {
              console.log("values", values);
              await handleSignupSubmit(values, setSubmitting);
            }}
          >
            {({ isSubmitting }) => (
              <Form>
                <AppBar position="static" sx={{ marginBottom: "15px" }}>
                  <Tabs
                    orientation={tabsOrientation}
                    value={signupAccountRole}
                    onChange={handleSetTabValue}
                  >
                    <Tab
                      label="Individual"
                      value={"employee"}
                      icon={
                        <i
                          className="ni ni-email-83"
                          style={{ marginTop: "6px", marginRight: "8px" }}
                        />
                      }
                    />
                    <Tab
                      label="Company"
                      value={"company"}
                      icon={
                        <i className="ni ni-app" style={{ marginTop: "6px", marginRight: "8px" }} />
                      }
                    />
                    <Tab
                      label="Creator"
                      value={"creator"}
                      icon={
                        <i
                          className="ni ni-email-83"
                          style={{ marginTop: "6px", marginRight: "8px" }}
                        />
                      }
                    />
                  </Tabs>
                </AppBar>
                <ProfileUpload
                  data={data}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.files && e.target.files[0]) {
                      setData({ ...data, image: e.target.files[0] });
                    }
                  }}
                />
                {signUpFields.map((field, index) => (
                  <Box mb={2} key={index}>
                    <FormikInput
                      name={field.name}
                      type={field.type}
                      placeholder={field.placeholder}
                      options={field.options}
                      optionsLabel={field.optionsLabel}
                      size="large"
                    />
                  </Box>
                ))}
                <Box display="flex" alignItems="center">
                  <Checkbox defaultChecked />
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    sx={{ cursor: "pointer", userSelect: "none" }}
                  >
                    &nbsp;&nbsp;I agree the&nbsp;
                  </Typography>
                  <Typography
                    component="a"
                    href="#"
                    variant="button"
                    fontWeight="bold"
                    textGradient
                  >
                    Terms and Conditions
                  </Typography>
                </Box>
                <Box mt={4} mb={1}>
                  <Button
                    loading={loading}
                    variant="gradient"
                    color="dark"
                    fullWidth
                    type="submit"
                    disabled={isSubmitting}
                  >
                    Sign Up
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
          <Box mt={2}>
            <Typography variant="button" color="text" fontWeight="regular">
              Already have an account?&nbsp;
              <Typography
                component={Link}
                to="/authentication/sign-in"
                variant="button"
                color="dark"
                fontWeight="bold"
                textGradient
              >
                Sign in
              </Typography>
            </Typography>
          </Box>
    </CoverLayout>
  );
}

export default Cover;
